// The "Bus pins" dialog. The logic is in iecwizard.js; this file draws it.
import { IecWizard } from './iecwizard.js';
import { IECFIND_LISTING, IECFIND_PRG_URL } from './iecprogram.js';
import { IecfindAutomation, PREPARE_STEPS, RESET_STEPS, UltimateBridge } from './ultimate.js';
import { networkForm } from './wifinetwork_ui.js';
import { button, paragraph, openWizardDialog, stepList } from './wizard_dialog.js';

const POLL_MS = 250;
const HOST_KEY = 'printy.ultimateHost'; // the address is remembered, the password is not

export function openIecWizard({ getDevice, reconnect }) {
  let wizard = null;
  let finished = false;
  let timer = null;

  const stopPolling = () => {
    clearInterval(timer);
    timer = null;
  };
  const close = async () => {
    stopPolling();
    const endsTest = !finished && wizard?.testing;
    if (!finished && wizard) await wizard.cancel(); // ends a running test: the ESP restarts
    finished = true;
    dlg.close();
    if (endsTest) reconnect();
  };
  const dlg = openWizardDialog({ onRequestClose: close });
  const show = dlg.show;

  const listing = () => {
    const d = document.createElement('details');
    d.innerHTML = '<summary>Type it in instead</summary>';
    const pre = document.createElement('pre');
    pre.textContent = IECFIND_LISTING;
    d.append(pre);
    return d;
  };

  const intro = () => {
    show(
      'Find the bus pins',
      [
        paragraph('This finds out which ESP pins are wired to DATA, CLK, ATN and RESET of the serial bus. The C64 runs a small program that wiggles each line in turn while the ESP counts what arrives on every pin. RESET is found by you resetting the C64 by hand.'),
        paragraph('<strong>Connect the serial cable to the C64 now.</strong> Then load <a href="' + IECFIND_PRG_URL + '" download>iecfind.prg</a> on the C64 (or type it in) and <strong>start it after pressing the button below</strong>. Programs that are already waiting for the bus (printing, disk loading) should not be running.'),
        listing(),
      ],
      [button('Cancel', close, 'outline secondary'), button('I have an Ultimate…', ultimateIntro, 'secondary'), button('Start counting', begin)],
    );
  };

  const begin = async () => {
    wizard = new IecWizard(getDevice(), { onChange: render });
    await wizard.start();
  };

  // ---- fully automatic with an Ultimate: the device runs the test program and resets the C64 through the
  // Ultimate's REST API over Wi-Fi (the page cannot call it itself, see ultimate.js) ----
  const bridge = new UltimateBridge(getDevice);

  const ultimateIntro = async () => {
    let creds = true;
    try {
      creds = !!(await getDevice().netStatus())?.hasCredentials;
    } catch {
      // unknown: assume stored, the check will say if Wi-Fi does not work
    }
    return creds ? ultimateHost() : ultimateWifi();
  };

  // the device needs a Wi-Fi network to reach the Ultimate (the same one Wi-Fi printing will use later)
  const ultimateWifi = (problem = null) => {
    const form = networkForm(getDevice);
    const next = button('Next', async () => {
      const v = form.values();
      if (!v) return;
      next.disabled = true;
      try {
        if ((await getDevice().setWifi(v.ssid, v.password)) !== 0) throw new Error('the device did not accept the network');
        ultimateHost();
      } catch (e) {
        ultimateWifi(e.message);
      }
    });
    show(
      'Wi-Fi network',
      [
        paragraph('The device talks to the Ultimate over Wi-Fi, so it needs the network the Ultimate is on (2.4&nbsp;GHz). The device saves the network (the password never leaves it) and Wi-Fi printing can use it later, so you do not have to enter it again.'),
        ...(problem ? [paragraph(`<strong>${problem}</strong>`)] : []),
        ...form.nodes,
      ],
      [button('Back', intro, 'outline secondary'), next],
    );
  };

  const ultimateHost = (problem = null) => {
    const host = Object.assign(document.createElement('input'), { type: 'text', placeholder: 'Address of the Ultimate, e.g. 192.168.1.50 or c64u.local', id: 'ultimate-host', autocomplete: 'off', spellcheck: false });
    host.value = localStorage.getItem(HOST_KEY) ?? '';
    const password = Object.assign(document.createElement('input'), { type: 'password', placeholder: 'Password of the Ultimate (empty if it has none)', id: 'ultimate-password', autocomplete: 'off' });
    const next = button('Check and start', async () => {
      const settings = { host: host.value.trim().replace(/^https?:\/\//, '').replace(/\/.*$/, ''), password: password.value };
      if (!settings.host) return ultimateHost('Type the address of the Ultimate.');
      localStorage.setItem(HOST_KEY, settings.host);
      show('Checking the Ultimate…', [paragraph('The device joins the network and asks the Ultimate who it is. This takes a few seconds.')], []);
      try {
        await bridge.connect(settings);
        const info = await bridge.info();
        startAutomatic(settings, info); // the session stays open: joining the network again is not needed
      } catch (e) {
        bridge.close().catch(() => {});
        if (e.kind === 'wifi') return ultimateWifi(e.message);
        ultimateHost(e.message);
      }
    });
    show(
      'The Ultimate',
      [
        paragraph('This works with a C64 Ultimate, an Ultimate 64 and an Ultimate-II+ / Ultimate 1541 that is on the network with its web interface (REST API) switched on. The device then runs the test program and resets the C64 itself.'),
        ...(problem ? [paragraph(`<strong>${problem}</strong>`)] : []),
        host,
        password,
      ],
      [button('Back', intro, 'outline secondary'), next],
    );
    host.focus();
  };

  const startAutomatic = async (settings, info) => {
    wizard = new IecWizard(getDevice(), { onChange: render, automation: new IecfindAutomation(bridge, settings) });
    wizard.ultimate = info;
    await wizard.start();
  };

  const moving = (w) => {
    const e = Object.entries(w.moving);
    return e.length ? `Pins that moved: ${e.map(([p, n]) => `GPIO${p} ×${n}`).join(', ')}` : 'No pin has moved yet.';
  };

  const render = (w) => {
    if (w.step !== 'detect' && w.step !== 'reset') stopPolling();
    else if (!timer) timer = setInterval(() => w.poll(), POLL_MS);
    if (w.step === 'preparing') {
      show(
        'Starting the test',
        [
          paragraph(`Found ${w.ultimate ? `<strong>${w.ultimate.product}</strong>${w.ultimate.firmware ? ' ' + w.ultimate.firmware : ''}` : 'the Ultimate'}. The device resets the C64 and starts the test program by itself.`),
          stepList(PREPARE_STEPS, w.autoStep),
        ],
        [button('Cancel', close, 'outline secondary')],
      );
    } else if (w.step === 'detect' && w.automation) {
      show(
        'Counting',
        [
          paragraph('The test program is running on the C64 and starts in a few seconds. It pulses DATA, CLK and ATN one after the other: about 7 seconds. Nothing to do here.'),
          paragraph(`<small>${moving(w)}</small>`),
          ...(w.hint ? [paragraph(`<strong>${w.hint}</strong>`)] : []),
        ],
        [button('Cancel', close, 'outline secondary'), button('Start over', () => w.retry(), 'outline')],
      );
    } else if (w.step === 'detect') {
      show(
        'Run the program on the C64',
        [
          paragraph('Type <code>RUN</code> on the C64 now (or reset it and run again). It pulses DATA, CLK and ATN one after the other: about 7 seconds. Do not press keys on the C64 until it says it is done.'),
          paragraph(`<small>${moving(w)}</small>`),
          ...(w.hint ? [paragraph(`<strong>${w.hint}</strong>`)] : []),
        ],
        [button('Cancel', close, 'outline secondary'), button('Start over', () => w.retry(), 'outline')],
      );
    } else if (w.step === 'found') {
      show(
        'Bus lines found',
        [
          paragraph(`DATA is on <strong>GPIO${w.pin.dat}</strong>, CLK on <strong>GPIO${w.pin.clk}</strong>, ATN on <strong>GPIO${w.pin.atn}</strong>.`),
          paragraph(w.automation ? 'RESET cannot be wiggled by the C64, but the device can have the Ultimate reset it and see which pin reacts. Is the RESET wire connected?' : 'RESET cannot be wiggled by the C64. If the RESET wire is connected, the ESP can find it while you reset the C64 by hand. Do you have the RESET wire connected?'),
        ],
        [
          button('Cancel', close, 'outline secondary'),
          button('No RESET wire', () => w.skipReset(), 'outline'),
          button('Find RESET', () => w.waitForReset()),
        ],
      );
    } else if (w.step === 'reset' && w.automation) {
      show(
        'Finding RESET',
        [
          paragraph('The Ultimate resets the C64 and the device watches which pin reacts.'),
          ...(w.auto ? [stepList(RESET_STEPS, w.autoStep)] : []),
          ...(w.hint ? [paragraph(`<strong>${w.hint}</strong>`)] : []),
        ],
        w.auto
          ? [button('Cancel', close, 'outline secondary')]
          : [button('Cancel', close, 'outline secondary'), button('No RESET wire', () => w.skipReset(), 'outline'), button('Try again', () => w.waitForReset())],
      );
    } else if (w.step === 'reset') {
      show(
        'Reset the C64 now',
        [
          paragraph('Press the reset button of the C64 (or use the reset in the Ultimate’s menu) once, then wait a moment. The ESP watches which pin reacts.'),
          ...(w.hint ? [paragraph(`<strong>${w.hint}</strong>`)] : []),
        ],
        [button('Cancel', close, 'outline secondary'), button('No RESET wire', () => w.skipReset(), 'outline')],
      );
    } else if (w.step === 'summary') {
      show(
        'Save the bus pins?',
        [
          paragraph(`DATA <strong>GPIO${w.pin.dat}</strong>, CLK <strong>GPIO${w.pin.clk}</strong>, ATN <strong>GPIO${w.pin.atn}</strong>, RESET <strong>${w.pin.rst != null ? 'GPIO' + w.pin.rst : 'not connected'}</strong>.`),
          paragraph('Saving stores this in the device and restarts it.'),
        ],
        [button('Cancel', close, 'outline secondary'), button('Start over', () => w.retry(), 'outline'), button('Save to the device', () => saveAndReconnect(w))],
      );
    } else if (w.step === 'saving') {
      show('Saving…', [paragraph('Storing the setting in the device.')], []);
    } else if (w.step === 'failed') {
      show('That did not work', [paragraph(w.error ?? 'Something went wrong.')], [button('Close', close, 'outline secondary'), button('Try again', () => w.retry())]);
    }
  };

  const saveAndReconnect = async (w) => {
    try {
      await w.save();
    } catch {
      return;
    }
    finished = true;
    show('Restarting the device…', [paragraph('The device restarts to use the new pins. The page reconnects by itself.')], []);
    const ok = await reconnect();
    show(
      ok ? 'Done' : 'Saved, but the page could not reconnect',
      [paragraph(ok ? 'The bus pins are set up.' : 'The setting is stored. Replug the device if needed and press Connect.')],
      [button('Close', close)],
    );
  };

  intro();
}
